import clsx from 'clsx';
import { Button, Stack } from 'components';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { getAddendums, getUsersImageNotes } from 'store/clientProfile/clientProfileActions';
import {
  Addendum as AddendumType,
  ClientProfileContentItem
} from 'store/clientProfile/clientProfileSlice';
import { clientReachedData, noteTypeData, typesOfSupportData } from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import Transcript from '../noteEdit/transcript/transcript';
import { Addendum } from './addendum';
import { AddendumForm } from './addendumForm';
import style from './noteView.module.scss';

type Props = {
  note: ClientProfileContentItem;
};

const TimeSpent = ({ note }: Props) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Time Spent:</p>
      <p className={style.text}>{note.timeSpent} Mins</p>
    </Stack>
  );
};

const NextContact = ({ note }: Props) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Next Contact:</p>
      <p className={style.text}>
        {note.nextContact && dayjs(note.nextContact).isValid()
          ? dayjs(note?.nextContact).format('MM/DD/YYYY')
          : ''}
      </p>
    </Stack>
  );
};

const TypeOfSupport = ({ note }: Props) => {
  const sortValues = (val: string[] | string, sortOrder: any[]) => {
    if (Array.isArray(val)) {
      return sortOrder
        .filter((item) => val.includes(item.id.toString()))
        .map((item) => item.name)
        .join(', ');
    } else {
      return val;
    }
  };

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Type of Support:</p>
      <p className={style.text}>{sortValues(note.typeOfSupport, typesOfSupportData)}</p>
    </Stack>
  );
};

const Sites = ({ note }: Props) => {
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Site:</p>
      <p className={style.text}>{note.siteName}</p>
    </Stack>
  );
};

const NoteType = ({ note }: Props) => {
  const noteType = noteTypeData.find((n) => n.id === note.noteType);
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Note Type:</p>
      <p className={style.text}>{noteType?.name}</p>
    </Stack>
  );
};

const ClientReached = ({ note }: Props) => {
  const clientReached = clientReachedData.find((n) => n.id === note.clientReached);
  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Client Reached:</p>
      <p className={style.text}>{clientReached?.name}</p>
    </Stack>
  );
};

const DateAndTime = ({ note }: Props) => {
  const timeFormatedValue =
    dayjs(note?.startTimeOfEncounter, ['hh:mm A', 'HH:mm:ss'], true).format('h:mm A') !==
    'Invalid Date'
      ? dayjs(note?.startTimeOfEncounter, ['hh:mm A', 'HH:mm:ss'], true).format('h:mm A')
      : '';

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>Date and Time:</p>
      <p className={style.text}>
        {dayjs(note?.dateOfEncounter).format('MM/DD/YYYY')} {timeFormatedValue}
      </p>
    </Stack>
  );
};

const ZCodes = ({ note }: Props) => {
  const zCodes = (note.selectedZCodes?.map((z) => `${z.code} - ${z.description}`) ?? []).join(', ');

  return (
    <Stack display="flex" gap="8px" flex="1">
      <p className={style.label}>SDOH Z codes:</p>
      <p className={style.text}>{zCodes}</p>
    </Stack>
  );
};

const NoteView = ({ note }: Props) => {
  const addendums = useAppSelector((state) => state.clientProfile.addendums.data);
  const [showAddendum, setShowAddendum] = useState(false);
  const dispatch = useAppDispatch();
  const { enableSites, aiScribe: enableAiScribe } = useFlags();

  useEffect(() => {
    dispatch(getAddendums({ noteId: note.id.toString() })).then((res: any) => {
      if (!res.error && res.payload.content.length > 0) {
        //TODO need to optimize
        res.payload.content.map((addendum: AddendumType) => {
          if (addendum.author.profileImageKey) {
            dispatch(
              getUsersImageNotes({
                profileImageKey: addendum.author.profileImageKey,
                noteId: addendum.id.toString(),
                noteAddendums: true
              })
            );
          }
        });
      }
    });
  }, []);

  return (
    <Stack mt="36px" display="flex" gap="20px">
      <Stack display="flex" flexDirection="row">
        <NoteType note={note} />
        <ClientReached note={note} />
      </Stack>
      <Stack display="flex" flexDirection="row">
        <DateAndTime note={note} />
        {enableSites ? <TimeSpent note={note} /> : <NextContact note={note} />}
      </Stack>
      <Stack display="flex" flexDirection="row">
        {enableSites ? <NextContact note={note} /> : <TimeSpent note={note} />}
        {enableSites ? <Sites note={note} /> : <TypeOfSupport note={note} />}
      </Stack>
      {enableSites && (
        <Stack display="flex" flexDirection="row">
          <TypeOfSupport note={note} />
        </Stack>
      )}
      <Stack display="flex" flexDirection="row">
        <ZCodes note={note} />
      </Stack>
      <Stack display="flex" flexDirection="row">
        <Stack display="flex" gap="8px" flex="1">
          <p className={style.label}>Note:</p>
          <p className={style.text}>{note.note}</p>
        </Stack>
      </Stack>
      <Stack display="flex" flexDirection="column">
        <Stack display="flex" flexDirection="column" gap="10px">
          {enableAiScribe && note.aiScribe?.transcript && <Transcript note={note} />}
          <Stack display="flex" flexDirection="column" gap="28px">
            {addendums.map((addendum) => (
              <Addendum key={addendum.id} addendum={addendum} />
            ))}
            {!showAddendum && (
              <Stack width="200px">
                <Button
                  handleOnClick={() => setShowAddendum(true)}
                  theme="transparent"
                  text="Add Addendum"
                  size="small"
                  icon={<span className="material-icons">add</span>}
                />
              </Stack>
            )}
          </Stack>
        </Stack>
        {showAddendum && (
          <div className={clsx(style.addendumWrapper)}>
            <AddendumForm editInitial={true} hideForm={() => setShowAddendum(false)} />
          </div>
        )}
      </Stack>
    </Stack>
  );
};

export default NoteView;
